import { HttpEvent, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';

import {
    FilterEntityType,
    IBuilding,
    IBuildingPOST,
    IBuildingPUT,
    IComment,
    IExternalSortingModel,
    IFilterConfigurationModel,
    IFilterModel,
    IImage,
    IIssue,
    IIssuePATCH,
    IIssuePOST,
    IIssueType,
    IIssueTypePOST,
    IIssueTypePUT,
    IJob,
    IJobPOST,
    IJobPUT,
    IJobSheet,
    IJobSheetFinalizeResponse,
    IMyTasksWorkspace,
    IPOI,
    IPOICategory,
    IPOICategoryPOST,
    IPOICategoryPUT,
    IPOIPATCH,
    IPOIPOST,
    IPaginatedTableResponse,
    ISharedEntity,
    ISortingModel,
    ISubjectType,
    ISubjectTypePOST,
    ISubjectTypePUT,
    ITask,
    ITaskApiConfig,
    ITaskApiConfigPUT,
    ITaskPATCH,
    ITaskPOST,
    ITaskSeries,
    ITaskSeriesDurationRequest,
    ITaskSeriesDurationResponse,
    ITaskSeriesPATCH,
    ITaskSeriesPOST,
    ITaskType,
    ITaskTypePOST,
    ITaskTypePUT,
    POIFilter,
    Pagination,
    PlannedTaskSortKey,
    TViewPortPolygon,
} from '../../models';
import { EntityFilters, PaginatedApiBaseService } from './paginated-api-base.service';

export type ApiResponseStatus<T> =
    | {
          status: 'completed';
          message: T;
      }
    | {
          status: 'progress';
          message: number;
      }
    | {
          status: 'ignored';
          message: string;
      };

@Injectable({ providedIn: 'root' })
export class TaskApiService extends PaginatedApiBaseService {
    public constructor() {
        super('taskApi');
    }

    // CRUD
    public getIssues(includeArchived?: boolean, includeDraftOnly?: boolean): Observable<Array<IIssue>> {
        const params = TaskApiService.getInclusionParams({ includeArchived, includeDraftOnly });
        const defaultSorting: ISortingModel<IIssue> = { property: 'status', direction: 'descending', order: 1 };

        return this.getApiUrl('/issues').pipe(
            this.paginatedHttpGet<IIssue, IIssue>(null, null, [defaultSorting], null, null, params),
            map((response) => response.data)
        );
    }

    public getIssuesPaginated<TableModel>(
        pagination: Pagination,
        filters?: EntityFilters,
        sorting?: Array<ISortingModel<TableModel>>,
        filterPreset?: string,
        includeArchived?: boolean,
        includeDraftOnly?: boolean,
        viewport?: TViewPortPolygon
    ): Observable<IPaginatedTableResponse<IIssue>> {
        const params = TaskApiService.getInclusionParams({ includeArchived, includeDraftOnly });
        return this.getApiUrl('/issues').pipe(
            this.paginatedHttpGet<IIssue, TableModel>(pagination, filters, sorting, filterPreset, viewport, params)
        );
    }

    public getIssueFilters<TFilterModel>(): Observable<TFilterModel> {
        return this.getApiUrl('/issues/filters').pipe(switchMap((url) => this.http.get<TFilterModel>(url)));
    }

    public getIssue(issueId: string): Observable<IIssue> {
        return this.getApiUrl('/issues/' + issueId).pipe(switchMap((url) => this.http.get<IIssue>(url)));
    }

    public postIssue(issue: IIssuePOST): Observable<IIssue> {
        return this.getApiUrl('/issues').pipe(switchMap((url) => this.http.post<IIssue>(url, issue)));
    }

    public exportIssues(type: string, language: string, ids: Array<string>): Observable<Blob> {
        const headers = new HttpHeaders().append('Accept-Language', language);

        return this.getApiUrl(`/issues/export/${type}`).pipe(
            switchMap((url) => this.http.put(url, { ids }, { responseType: 'blob', headers }))
        );
    }

    public getIssueTypes(): Observable<Array<IIssueType>> {
        return this.getApiUrl('/issuetypes').pipe(switchMap((url) => this.http.get<Array<IIssueType>>(url)));
    }

    public postIssueType(type: IIssueTypePOST): Observable<IIssueType> {
        return this.getApiUrl('/issuetypes').pipe(switchMap((url) => this.http.post<IIssueType>(url, type)));
    }

    public deleteIssueType(issueTypeId: string): Observable<IIssueType> {
        return this.getApiUrl(`/issuetypes/${issueTypeId}`).pipe(switchMap((url) => this.http.delete<IIssueType>(url)));
    }

    public putIssueType(issueTypeId: string, issueType: IIssueTypePUT): Observable<IIssueType> {
        return this.getApiUrl(`/issuetypes/${issueTypeId}`).pipe(
            switchMap((url) => this.http.put<IIssueType>(url, issueType))
        );
    }

    public archiveIssueType(issueTypeId: string): Observable<IIssueType> {
        return this.getApiUrl(`/issuetypes/${issueTypeId}/archive`).pipe(
            switchMap((url) => this.http.put<IIssueType>(url, issueTypeId))
        );
    }

    public getTasks(
        includeDraftOnly = false,
        scheduleDate?: number,
        includeArchived?: boolean,
        sorting?: ISortingModel<ITask>
    ): Observable<Array<ITask>> {
        let params = TaskApiService.getInclusionParams({ includeArchived, includeDraftOnly });
        if (typeof scheduleDate === 'number') params = params.append('scheduleDate', scheduleDate.toString());
        sorting ??= { property: 'status', direction: 'ascending', order: 1 };

        return this.getApiUrl('/tasks').pipe(
            this.paginatedHttpGet<ITask, ITask>(null, null, [sorting], null, null, params),
            map((response) => response.data)
        );
    }

    public getPlannedTasks(
        includeDraftOnly = false,
        includeArchived = false,
        includePreview = false,
        scheduleDate?: number,
        sorting?: ISortingModel<ITask>
    ): Observable<Array<ITask>> {
        let params = TaskApiService.getInclusionParams({ includeArchived, includeDraftOnly, includePreview });
        if (typeof scheduleDate === 'number') params = params.append('scheduleDate', scheduleDate.toString());
        params = params.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
        sorting ??= { property: 'status', direction: 'ascending', order: 1 };

        return this.getApiUrl('/tasks/planned').pipe(
            this.paginatedHttpGet<ITask, ITask>(null, null, [sorting], null, null, params),
            map((response) => response.data)
        );
    }

    public getPlannedTasksPaginated(
        pagination: Pagination,
        filters: EntityFilters,
        sorting: Array<IExternalSortingModel<PlannedTaskSortKey>>,
        includeDraftOnly = false,
        includeArchived = false,
        includePreview = false,
        scheduleDate?: number
    ): Observable<IPaginatedTableResponse<ITask>> {
        let params = TaskApiService.getInclusionParams({ includeArchived, includeDraftOnly, includePreview });
        if (typeof scheduleDate === 'number') params = params.append('scheduleDate', scheduleDate.toString());
        params = params.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

        return this.getApiUrl('/tasks/planned').pipe(
            this.paginatedHttpGet<ITask, Record<PlannedTaskSortKey, never>>(
                pagination,
                filters,
                sorting,
                null,
                null,
                params
            )
        );
    }

    public getTasksPaginated<TableModel>(
        pagination: Pagination,
        filters: EntityFilters,
        sorting: Array<ISortingModel<TableModel>>,
        filterPreset?: string,
        includeArchived?: boolean,
        includeDraftOnly?: boolean,
        viewport?: TViewPortPolygon
    ): Observable<IPaginatedTableResponse<ITask>> {
        const params = TaskApiService.getInclusionParams({ includeArchived, includeDraftOnly });
        return this.getApiUrl('/tasks').pipe(
            this.paginatedHttpGet<ITask, TableModel>(pagination, filters, sorting, filterPreset, viewport, params)
        );
    }

    public getTaskFilters<TFilterModel>(): Observable<TFilterModel> {
        return this.getApiUrl('/tasks/filters').pipe(switchMap((url) => this.http.get<TFilterModel>(url)));
    }

    public getTask(taskId: string): Observable<ITask> {
        return this.getApiUrl('/tasks/' + taskId).pipe(switchMap((url) => this.http.get<ITask>(url)));
    }

    public postTask(task: ITaskPOST): Observable<ITask> {
        return this.getApiUrl('/tasks').pipe(switchMap((url) => this.http.post<ITask>(url, task)));
    }

    public exportTasks(type: string, language: string, ids: Array<string>): Observable<Blob> {
        const headers = new HttpHeaders().append('Accept-Language', language);

        return this.getApiUrl(`/tasks/export/${type}`).pipe(
            switchMap((url) => this.http.put(url, { ids }, { responseType: 'blob', headers }))
        );
    }

    public getTaskTypes(): Observable<Array<ITaskType>> {
        return this.getApiUrl('/tasktypes').pipe(switchMap((url) => this.http.get<Array<ITaskType>>(url)));
    }

    public postTaskType(type: ITaskTypePOST): Observable<ITaskType> {
        return this.getApiUrl('/tasktypes').pipe(switchMap((url) => this.http.post<ITaskType>(url, type)));
    }

    public deleteTaskType(taskTypeId: string): Observable<ITaskType> {
        return this.getApiUrl(`/tasktypes/${taskTypeId}`).pipe(switchMap((url) => this.http.delete<ITaskType>(url)));
    }

    public putTaskType(taskTypeId: string, taskType: ITaskTypePUT): Observable<ITaskType> {
        return this.getApiUrl(`/tasktypes/${taskTypeId}`).pipe(
            switchMap((url) => this.http.put<ITaskType>(url, taskType))
        );
    }

    public archiveTaskType(taskTypeId: string): Observable<ITaskType> {
        return this.getApiUrl(`/tasktypes/${taskTypeId}/archive`).pipe(
            switchMap((url) => this.http.put<ITaskType>(url, taskTypeId))
        );
    }

    public getSubjectTypes(): Observable<Array<ISubjectType>> {
        return this.getApiUrl('/subjecttypes').pipe(switchMap((url) => this.http.get<Array<ISubjectType>>(url)));
    }

    public postSubjectType(type: ISubjectTypePOST): Observable<ISubjectType> {
        return this.getApiUrl('/subjecttypes').pipe(switchMap((url) => this.http.post<ISubjectType>(url, type)));
    }

    public deleteSubjectType(subjectTypeId: string): Observable<ISubjectType> {
        return this.getApiUrl(`/subjecttypes/${subjectTypeId}`).pipe(
            switchMap((url) => this.http.delete<ISubjectType>(url))
        );
    }

    public putSubjectType(subjectTypeId: string, subjectType: ISubjectTypePUT): Observable<ISubjectType> {
        return this.getApiUrl(`/subjecttypes/${subjectTypeId}`).pipe(
            switchMap((url) => this.http.put<ISubjectType>(url, subjectType))
        );
    }

    public archiveSubjectType(subjectTypId: string): Observable<ISubjectType> {
        return this.getApiUrl(`/subjecttypes/${subjectTypId}/archive`).pipe(
            switchMap((url) => this.http.put<ISubjectType>(url, subjectTypId))
        );
    }

    public postPOI(poi: IPOIPOST): Observable<IPOI> {
        return this.getApiUrl('/pois').pipe(switchMap((url) => this.http.post<IPOI>(url, poi)));
    }

    public patchPOI(poi: IPOIPATCH): Observable<IPOI> {
        const { description, categoryId, location, buildingId } = poi;
        return this.getApiUrl('/pois/' + poi.id).pipe(
            switchMap((url) => this.http.patch<IPOI>(url, { description, categoryId, location, buildingId }))
        );
    }

    public archivePOI(poiId: string): Observable<IPOI> {
        return this.getApiUrl(`/pois/${poiId}/archive`).pipe(switchMap((url) => this.http.put<IPOI>(url, poiId)));
    }

    public deletePOI(poi: IPOI): Observable<void> {
        return this.getApiUrl('/pois/' + poi.id).pipe(switchMap((url) => this.http.delete<void>(url)));
    }

    public getPOIs(filters: POIFilter = {}): Observable<Array<IPOI>> {
        // Stringify filter values
        const params = Object.entries(filters).reduce(
            (
                acc: {
                    [key: string]: string;
                },
                e
            ) => ((acc[e[0]] = e[1].toString()), acc),
            {}
        );
        // Send request
        return this.getApiUrl('/pois').pipe(switchMap((url) => this.http.get<Array<IPOI>>(url, { params })));
    }

    public getPOICategories(): Observable<Array<IPOICategory>> {
        return this.getApiUrl('/poiCategories').pipe(switchMap((url) => this.http.get<Array<IPOICategory>>(url)));
    }

    public postPOICategory(category: IPOICategoryPOST): Observable<IPOICategory> {
        return this.getApiUrl('/poiCategories').pipe(switchMap((url) => this.http.post<IPOICategory>(url, category)));
    }

    public deletePOICategory(categoryId: string): Observable<IPOICategory> {
        return this.getApiUrl(`/poiCategories/${categoryId}`).pipe(
            switchMap((url) => this.http.delete<IPOICategory>(url))
        );
    }

    public archivePOICategory(categoryId: string): Observable<IPOICategory> {
        return this.getApiUrl(`/poiCategories/${categoryId}/archive`).pipe(
            switchMap((url) => this.http.put<IPOICategory>(url, categoryId))
        );
    }

    public putPOICategory(categoryId: string, category: IPOICategoryPUT): Observable<IPOICategory> {
        return this.getApiUrl(`/poiCategories/${categoryId}`).pipe(
            switchMap((url) => this.http.put<IPOICategory>(url, category))
        );
    }

    public getConfig(): Observable<ITaskApiConfig> {
        return this.getApiUrl('/config').pipe(switchMap((url) => this.http.get<ITaskApiConfig>(url)));
    }

    public putConfig(config: ITaskApiConfigPUT): Observable<ITaskApiConfig> {
        return this.getApiUrl('/config').pipe(switchMap((url) => this.http.put<ITaskApiConfig>(url, config)));
    }

    public patchTask(taskId: string, patch: Partial<ITaskPATCH>): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}`).pipe(switchMap((url) => this.http.patch<ITask>(url, patch)));
    }

    public patchIssue(issueId: string, patch: Partial<IIssuePATCH>): Observable<IIssue> {
        return this.getApiUrl(`/issues/${issueId}`).pipe(switchMap((url) => this.http.patch<IIssue>(url, patch)));
    }

    public postIssueImage(issueId: string, file: File): Observable<HttpEvent<IImage>> {
        const formData = new FormData();
        formData.append('image', file);
        return this.getApiUrl(`/issues/${issueId}/images`).pipe(
            switchMap((url) => this.http.post<IImage>(url, formData, { reportProgress: true, observe: 'events' }))
        );
    }

    public getJobs(scheduleDate: number): Observable<Array<IJob>> {
        return this.getApiUrl('/jobs').pipe(
            switchMap((url) =>
                this.http.get<Array<IJob>>(url, {
                    params: {
                        scheduleDate: scheduleDate.toString(10),
                    },
                })
            )
        );
    }

    public postJobs(jobs: Array<Partial<IJobPOST>>): Observable<Array<IJob>> {
        return this.getApiUrl('/jobs', 'v2').pipe(switchMap((url) => this.http.post<Array<IJob>>(url, jobs)));
    }

    public putJob(jobId: string, job: IJobPUT): Observable<IJob> {
        return this.getApiUrl(`/jobs/${jobId}`).pipe(switchMap((url) => this.http.put<IJob>(url, job)));
    }

    public getJobSheets(scheduleDate: number): Observable<Array<IJobSheet>> {
        return this.getApiUrl('/jobsheets').pipe(
            switchMap((url) =>
                this.http.get<Array<IJobSheet>>(url, {
                    params: {
                        scheduleDate: scheduleDate.toString(10),
                    },
                })
            )
        );
    }

    public generateJobSheets(
        scheduleDate: number,
        startingPointPoiId: string,
        workerTimeCapacity: number
    ): Observable<Array<IJobSheet>> {
        return this.getApiUrl(`/jobsheets/generate`, 'v2').pipe(
            switchMap((url) =>
                this.http.post<Array<IJobSheet>>(url, {
                    scheduleDate,
                    startingPointPoiId,
                    workerTimeCapacity,
                })
            )
        );
    }

    public removeJobSheets(scheduledDate: number): Observable<Array<IJobSheet>> {
        return this.getApiUrl(`/jobsheets/cancel`, 'v2').pipe(
            switchMap((url) =>
                this.http.delete<Array<IJobSheet>>(url, {
                    params: {
                        scheduledDate,
                    },
                })
            )
        );
    }

    public finalizeJobSheets(scheduledDate: number): Observable<IJobSheetFinalizeResponse> {
        return this.getApiUrl(`/jobsheets/finalize`, 'v2').pipe(
            switchMap((url) =>
                this.http.post<IJobSheetFinalizeResponse>(url, {
                    scheduledDate,
                })
            )
        );
    }

    // General Actions
    public generateShareUrl(
        itemId: string,
        itemType: 'TASK' | 'ISSUE',
        expiresAt: number,
        allowFinish: boolean
    ): Observable<ISharedEntity> {
        return this.getApiUrl(`/shareLinks`).pipe(
            switchMap((url) => this.http.post<ISharedEntity>(url, { itemId, itemType, expiresAt, allowFinish }))
        );
    }

    public updateShareUrl(shareId: string, expiresAt: number, allowFinish: boolean): Observable<ISharedEntity> {
        return this.getApiUrl(`/shareLinks/${shareId}`).pipe(
            switchMap((url) => this.http.put<ISharedEntity>(url, { expiresAt, allowFinish }))
        );
    }

    public getSharedEntity(shareId: string, tenantId: string): Observable<ISharedEntity> {
        return this.getApiUrl(`/shareLinks/${tenantId}/${shareId}`).pipe(
            switchMap((url) => this.http.get<ISharedEntity>(url))
        );
    }

    // Task Actions
    public shareLinkAction(shareId: string, action: string, tenantId: string): Observable<ISharedEntity> {
        return this.getApiUrl(`/shareLinks/${tenantId}/${shareId}/action`).pipe(
            switchMap((url) => this.http.post<ISharedEntity>(url, { action }))
        );
    }

    public pickUpTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/pickUp`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    public finishTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/finish`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    public approveTaskInspection(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/approveInspection`).pipe(
            switchMap((url) => this.http.put<ITask>(url, {}))
        );
    }

    public rejectTaskInspection(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/rejectInspection`).pipe(
            switchMap((url) => this.http.put<ITask>(url, {}))
        );
    }

    public blockTask(taskId: string, blockReasonId: string, blockReasonNote: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/block`).pipe(
            switchMap((url) =>
                this.http.put<ITask>(url, {
                    blockReasonId: blockReasonId || undefined,
                    blockReasonNote: blockReasonNote || undefined,
                })
            )
        );
    }

    public assignTask(taskId: string, userId: string | null, teamId: string | null): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/assign`).pipe(
            switchMap((url) => this.http.put<ITask>(url, { userId, teamId }))
        );
    }

    public assignInspector(taskId: string, userId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/assigninspector`).pipe(
            switchMap((url) => this.http.put<ITask>(url, { userId }))
        );
    }

    public unassignInspector(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/unassigninspector`).pipe(
            switchMap((url) => this.http.put<ITask>(url, {}))
        );
    }

    public dropTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/drop`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    public cancelTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/cancel`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    public unblockTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/unblock`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    public resumeTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/resume`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    public deleteTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}`).pipe(switchMap((url) => this.http.delete<ITask>(url)));
    }

    public publishTask(taskId: string): Observable<ITask> {
        return this.getApiUrl(`/tasks/${taskId}/publish`).pipe(switchMap((url) => this.http.put<ITask>(url, {})));
    }

    // Issue Actions
    public closeIssue(issueId: string): Observable<IIssue> {
        return this.getApiUrl(`/issues/${issueId}/close`).pipe(switchMap((url) => this.http.put<IIssue>(url, {})));
    }

    public cancelIssue(issueId: string): Observable<IIssue> {
        return this.getApiUrl(`/issues/${issueId}/cancel`).pipe(switchMap((url) => this.http.put<IIssue>(url, {})));
    }

    // Job Sheet Actions
    public assignJobSheet(jobSheetId: string, userId: string): Observable<IJobSheet> {
        return this.getApiUrl(`/jobsheets/${jobSheetId}/assign`).pipe(
            switchMap((url) => this.http.put<IJobSheet>(url, { userId }))
        );
    }

    public unassignJobSheet(jobSheetId: string): Observable<IJobSheet> {
        return this.getApiUrl(`/jobsheets/${jobSheetId}/unassign`).pipe(
            switchMap((url) => this.http.put<IJobSheet>(url, {}))
        );
    }

    public assignJobSheetInspector(jobSheetId: string, userId: string): Observable<IJobSheet> {
        return this.getApiUrl(`/jobsheets/${jobSheetId}/assignInspector`).pipe(
            switchMap((url) => this.http.put<IJobSheet>(url, { userId }))
        );
    }

    public unassignJobSheetInspector(jobSheetId: string): Observable<IJobSheet> {
        return this.getApiUrl(`/jobsheets/${jobSheetId}/unassignInspector`).pipe(
            switchMap((url) => this.http.put<IJobSheet>(url, {}))
        );
    }

    public exportJobSheetsPDF(scheduleDate: number): Observable<Blob> {
        return this.getApiUrl('/jobsheets/export').pipe(
            switchMap((url) => this.http.post(url, { scheduleDate }, { responseType: 'blob' }))
        );
    }

    public postComment(
        itemType: string,
        itemId: string,
        comment?: string,
        images?: Array<Blob>
    ): Observable<ApiResponseStatus<IComment>> {
        const headers = new HttpHeaders({ 'ngsw-bypass': '' });
        const formData: FormData = new FormData();
        formData.append('itemType', itemType);
        formData.append('itemId', itemId);
        if (comment) {
            formData.append('comment', comment);
        }
        images?.forEach((image) => formData.append('images', image));

        return this.getApiUrl(`/comments`).pipe(
            switchMap((url) =>
                this.http.post<IComment>(url, formData, {
                    reportProgress: true,
                    observe: 'events',
                    headers,
                })
            ),
            map((event) => {
                switch (event.type) {
                    case HttpEventType.UploadProgress:
                        const progress = Math.round((event.loaded / event.total) * 1000) / 1000;
                        return { status: 'progress', message: progress };

                    case HttpEventType.Response:
                        return { status: 'completed', message: event.body };
                    default:
                        return { status: 'ignored', message: `Ignored HTTPEventType enum: ${event.type}` };
                }
            })
        );
    }

    public listComment(itemType: string, itemId: string): Observable<Array<IComment>> {
        const queryParams = new HttpParams().append('itemId', itemId).append('itemType', itemType);
        return this.getApiUrl(`/comments`).pipe(
            switchMap((url) => this.http.get<Array<IComment>>(url, { params: queryParams }))
        );
    }

    public getComment(commentId: string): Observable<IComment> {
        return this.getApiUrl(`/comments/${commentId}`).pipe(switchMap((url) => this.http.get<IComment>(url)));
    }

    public deleteCommentImage(commentId: string, imageId: string): Observable<void> {
        return this.getApiUrl(`/comments/${commentId}/images/${imageId}`).pipe(
            switchMap((url) => this.http.delete<void>(url))
        );
    }

    public getFilterPreset<Entity>(
        presetId: string,
        filterEntityType: FilterEntityType
    ): Observable<IFilterConfigurationModel<Entity>> {
        return this.getApiUrl(`${this.getEntityType(filterEntityType)}/filters/presets/${presetId}`).pipe(
            switchMap((url) => this.http.get<IFilterConfigurationModel<Entity>>(url))
        );
    }

    public postFilterPreset<Entity>(
        name: string,
        filterEntityType: FilterEntityType
    ): Observable<IFilterConfigurationModel<Entity>> {
        return this.getApiUrl(`${this.getEntityType(filterEntityType)}/filters/presets`).pipe(
            concatMap((url) => this.http.post<IFilterConfigurationModel<Entity>>(url, { name: name }))
        );
    }

    public postFilterPresetSorting<Entity>(
        presetId: string,
        sort: IExternalSortingModel<keyof Entity | string>,
        filterEntityType: FilterEntityType
    ): Observable<ISortingModel<Entity>> {
        return this.getApiUrl(`${this.getEntityType(filterEntityType)}/filters/presets/${presetId}/sorting`).pipe(
            switchMap((url) => this.http.post<ISortingModel<Entity>>(url, sort))
        );
    }

    public postFilterPresetFilters(
        presetId: string,
        filterValues: IFilterModel,
        filterEntityType: FilterEntityType
    ): Observable<IFilterModel> {
        return this.getApiUrl(`${this.getEntityType(filterEntityType)}/filters/presets/${presetId}/filter`).pipe(
            switchMap((url) => this.http.post<IFilterModel>(url, filterValues))
        );
    }

    public patchFilterPreset<Entity>(
        name: string,
        presetId: string,
        filterEntityType: FilterEntityType
    ): Observable<IFilterConfigurationModel<Entity>> {
        return this.getApiUrl(`${this.getEntityType(filterEntityType)}/filters/presets/${presetId}`).pipe(
            concatMap((url) => this.http.patch<IFilterConfigurationModel<Entity>>(url, { name: name }))
        );
    }

    public deleteFilterPreset<Entity>(
        filterPresetId: string,
        filterEntityType: FilterEntityType
    ): Observable<IFilterConfigurationModel<Entity>> {
        return this.getApiUrl(`${this.getEntityType(filterEntityType)}/filters/presets/${filterPresetId}`).pipe(
            switchMap((url) => this.http.delete<IFilterConfigurationModel<Entity>>(url))
        );
    }

    public deleteFilterPresetFilters(
        filterPresetId: string,
        filterId: string,
        filterEntityType: FilterEntityType
    ): Observable<IFilterModel> {
        return this.getApiUrl(
            `${this.getEntityType(filterEntityType)}/filters/presets/${filterPresetId}/filter/${filterId}`
        ).pipe(switchMap((url) => this.http.delete<IFilterModel>(url)));
    }

    public deleteFilterPresetSorting<Entity>(
        presetId: string,
        sortingId: string,
        filterEntityType: FilterEntityType
    ): Observable<ISortingModel<Entity>> {
        return this.getApiUrl(
            `${this.getEntityType(filterEntityType)}/filters/presets/${presetId}/sorting/${sortingId}`
        ).pipe(switchMap((url) => this.http.delete<ISortingModel<Entity>>(url)));
    }

    public getBuildings(): Observable<Array<IBuilding>> {
        return this.getApiUrl('/buildings').pipe(switchMap((url) => this.http.get<Array<IBuilding>>(url)));
    }

    public postBuilding(type: IBuildingPOST): Observable<IBuilding> {
        return this.getApiUrl('/buildings').pipe(switchMap((url) => this.http.post<IBuilding>(url, type)));
    }

    public deleteBuilding(buildingId: string): Observable<IBuilding> {
        return this.getApiUrl(`/buildings/${buildingId}`).pipe(switchMap((url) => this.http.delete<IBuilding>(url)));
    }

    public putBuilding(buildingId: string, building: IBuildingPUT): Observable<IBuilding> {
        return this.getApiUrl(`/buildings/${buildingId}`).pipe(
            switchMap((url) => this.http.put<IBuilding>(url, building))
        );
    }

    public getTaskSeriesPaginated<TableModel>(
        pagination: Pagination,
        filters?: EntityFilters,
        sorting?: Array<ISortingModel<TableModel>>
    ): Observable<IPaginatedTableResponse<ITaskSeries>> {
        return this.getApiUrl('/taskseries').pipe(
            this.paginatedHttpGet<ITaskSeries, TableModel>(pagination, filters, sorting)
        );
    }

    public getTaskSeriesFilters<TFilterModel>(): Observable<TFilterModel> {
        return this.getApiUrl('/taskseries/filters').pipe(switchMap((url) => this.http.get<TFilterModel>(url)));
    }

    public getTaskSeries(id: string): Observable<ITaskSeries> {
        return this.getApiUrl(`/taskseries/${id}`).pipe(switchMap((url) => this.http.get<ITaskSeries>(url)));
    }

    public postTaskSeries(serie: ITaskSeriesPOST): Observable<ITaskSeries> {
        return this.getApiUrl('/taskseries').pipe(switchMap((url) => this.http.post<ITaskSeries>(url, serie)));
    }

    public patchTaskSeries(id: string, serie: ITaskSeriesPATCH): Observable<ITaskSeries> {
        return this.getApiUrl(`/taskseries/${id}`).pipe(switchMap((url) => this.http.patch<ITaskSeries>(url, serie)));
    }

    public deleteTaskSeries(id: string): Observable<void> {
        return this.getApiUrl(`/taskseries/${id}`).pipe(
            switchMap((url) => this.http.delete<void>(url)),
            map((): void => void 0)
        );
    }

    public calculateTaskSeriesDuration(data: ITaskSeriesDurationRequest): Observable<ITaskSeriesDurationResponse> {
        return this.getApiUrl('/taskseries/duration').pipe(
            switchMap((url) => this.http.post<ITaskSeriesDurationResponse>(url, data))
        );
    }

    public getMyTasks(): Observable<Array<IMyTasksWorkspace>> {
        return this.getApiUrl('/mytasks').pipe(switchMap((url) => this.http.get<Array<IMyTasksWorkspace>>(url)));
    }

    private getEntityType(filterType: FilterEntityType): string {
        switch (filterType) {
            case FilterEntityType.Issue:
                return 'issues';
            case FilterEntityType.Task:
                return 'tasks';
            default:
                throw new Error(`invalid entity type: ${filterType}`);
        }
    }

    private static getInclusionParams(options: {
        includeArchived?: boolean;
        includeDraftOnly?: boolean;
        includePreview?: boolean;
    }): HttpParams {
        let params = new HttpParams();
        if (options.includeArchived) {
            params = params.append('includeArchived', 'true');
        }

        if (options.includeDraftOnly) {
            params = params.append('includeDraft', 'true');
        }

        if (options.includePreview) {
            params = params.append('includePreview', 'true');
        }

        return params;
    }
}
